import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Tooltip, Grid, TextField, CircularProgress } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import SearchIcon from '@mui/icons-material/Search';
import PurchaseService from 'services/PurchaseService';
import ListDialog from 'components/popups/ListDialog';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ShopContext } from 'Context/ShopContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import PrintIcon from '@mui/icons-material/Print';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import LinkIcon from '@mui/icons-material/Link';
import { useHistory } from 'react-router-dom';
import { ROUTES, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { useTranslation } from 'react-i18next';

function PurchaseSummary() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');

  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const history = useHistory();

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());

  const [searchText, setSearchText] = useState('');
  const [purchases, setPurchase] = useState('');
  const [viewItems, setViewItems] = useState('');
  const [currency, setCurrency] = useState('');
  const [listTitle, setListTitle] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    id: 'Id',
    invoice_no: 'Invoice No',
    bill_date: 'Bill Date',
    date: 'Date',
    vendor: {
      title: 'Vendor Name',
      getValue: (purchaseItem) => purchaseItem?.vendor?.name ?? purchaseItem?.vendor_name_old ?? '-',
    },
    plu: {
      title: 'PLU',
      getValue: (purchaseItem) => purchaseItem?.item?.plu,
    },
    name: {
      title: 'Item Name',
      getValue: (purchaseItem) => purchaseItem?.item?.name,
    },
    rate: {
      title: 'Cost',
      getValue: (purchaseItem) => purchaseItem?.item?.rate,
    },
    quantity: {
      title: 'Quantity',
      getValue: (purchaseItem) => purchaseItem?.item?.quantity,
    },
    tax: {
      title: 'Tax',
      getValue: (purchaseItem) => {
        return ShopsHelper.getAmountFormatted(shop, purchaseItem?.item?.tax || 0);
      },
    },
    total: {
      title: 'Total',
      getValue: (purchaseItem) => {
        const amount = purchaseItem?.item?.rate * purchaseItem?.item?.quantity + (purchaseItem?.item?.tax || 0);
        return ShopsHelper.getAmountFormatted(shop, amount);
      },
    },
    total_amount: {
      title: 'Purchase Total',
      getValue: (purchaseItem) => {
        return ShopsHelper.getAmountFormatted(shop, purchaseItem?.total_amount || 0);
      },
    },
  };

  const subTotal = (items) => {
    return items.reduce(function (a, b) {
      return a + b['total'];
    }, 0);
  };

  const handleItemsView = (row = '') => {
    if (row) {
      const selectedItems = row.items;
      setListTitle(`Purchased items from ${row?.vendor?.name ?? row.vendor_name_old} on ${row.date}`);
      let data = selectedItems.map((item) => ({
        ...item,
        name: item?.item?.name,
        total: Number(item.quantity) * Number(item.rate) + Number(item.tax),
      }));
      data = [
        ...data,
        {
          plu: '',
          name: '',
          quantity: '',
          rate: '',
          tax: 'Total',
          total: subTotal(data),
        },
      ];
      return setViewItems(data);
    }
    return setViewItems('');
  };

  const toCsvBtnPressed = async () => {
    const csvFormattedPurchases = purchases.flatMap((obj) =>
      obj.items.map((item, index) => ({
        id: obj.id,
        invoice_no: obj.invoice_no,
        bill_date: obj.bill_date,
        date: obj.date,
        vendor: obj.vendor,
        vendor_name_old: obj.vendor_name_old,
        total_amount: index === 0 ? obj.total_amount : undefined,
        item: {
          ...item,
          name: item.item.name,
        },
      }))
    );

    const csv = CsvHelper.getString(csvFormattedPurchases, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PURCHASE-SUMMARY-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getPurchasesFiltered = (purchases, searchKey) => {
    if (!searchKey) {
      return purchases;
    }

    const searchKeySmallCase = searchKey.toLowerCase();
    return (purchases || []).filter(
      (purchase) =>
        purchase?.vendor.name.toLowerCase().includes(searchKeySmallCase) ||
        purchase.date.includes(searchKeySmallCase) ||
        purchase.total_amount.toString().includes(searchKeySmallCase)
    );
  };

  const _getPurchasesAmountSum = (purchases) => {
    if (!purchases || !purchases.length) {
      return 0;
    }

    let sum = 0;
    purchases.forEach((purchase) => (sum += purchase.total_amount));
    return ShopsHelper.getAmountFormatted(shop, sum);
  };

  const redirectToVendorDetails = (vendor) => {
    if (vendor?.id) {
      history.push(ROUTES.VENDOR_DETAILS.replace(':vendorId', vendor.id));
    }
  };

  const handleCloseDetailsDialog = () => {
    setViewItems(null);
  };

  const headerData = [
    {
      label: tCommon('Vendor Name'),
      id: 'vendor',
      type: 'callback',
      viewRender: (item) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{item?.vendor?.name ?? item?.vendor_name_old ?? '-'}</span>
          {item?.vendor?.id && (
            <Tooltip title="View Vendor">
              <LinkIcon
                style={{ cursor: 'pointer', marginLeft: 4 }}
                onClick={() => redirectToVendorDetails(item.vendor)}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      label: tCommon('Purchase Date'),
      id: 'date',
      type: 'callback',
      viewRender: (item) => `${DateTimeHelpers.formatDMYTo12Hour(item.date)}`,
    },
    {
      label: tCommon('Bill Date'),
      id: 'bill_date',
      type: 'text',
    },
    {
      label: tCommon('Invoice No'),
      id: 'invoice_no',
      type: 'text',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('View Items'),
      title: 'View items',
      id: 'viewItems',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Tooltip title="View items" onClick={() => handleItemsView(item)}>
            <RemoveRedEyeIcon className={styles.iconButton} />
          </Tooltip>
        );
      },
    },
  ];

  const listHeaderData = [
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Price'),
      id: 'rate',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'text',
    },
    {
      label: tCommon('Tax'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total)}`;
      },
    },
  ];

  const _getLoadPurchaseRequestParams = useCallback(() => {
    return {
      date_start: DateTimeHelpers.getDateYMD(filterFromDate),
      date_end: DateTimeHelpers.getDateYMD(filterToDate),
    };
  }, [filterFromDate, filterToDate]);

  const generatePurchaseHistory = () => {
    const { startDate, endDate } = dynamicDateRange('PURCHASE_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    getPurchase({
      date_start: DateTimeHelpers.getDateYMD(startDate),
      date_end: DateTimeHelpers.getDateYMD(endDate),
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
    });
  };

  const getPurchase = useCallback(
    async (customDateParams) => {
      setLoadingIndicator(true);
      try {
        const params = customDateParams || _getLoadPurchaseRequestParams();

        const res = await PurchaseService.getPurchaseDetails(params);
        setPurchase(res);
        setCurrency(res.currency);
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [_getLoadPurchaseRequestParams, postAlertMessage]
  );

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return filterFromDate !== prevFilterData.filterFromDate || filterToDate !== prevFilterData.filterToDate;
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  useEffect(() => {
    generatePurchaseHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Purchase Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      purchasesToDisplay,
      {
        [tCommon('Total Amount')]: purchaseAmountSum,
      }
    );
  };

  const purchasesToDisplay = _getPurchasesFiltered(purchases, searchText);
  const purchaseAmountSum = _getPurchasesAmountSum(purchases);
  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>Print Report</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a', marginRight: 5 }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            Csv
          </Button>
        </div>
      </div>

      <div className={styles.changeable}>
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={12}>
            <h2 className={styles.subTitle}>
              {tReport('Purchase Summary Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </Grid>

          <Grid item xs={12} md={3}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="PURCHASE_SUMMARY"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={tCommon('Search purchase')}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              className={styles.greenBtn}
              onClick={generatePurchaseHistory}
              disabled={isShowGenerateButton === false || loadingIndicator}
              startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {tCommon('Load Purchases')}
            </Button>
          </Grid>
        </Grid>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        purchases &&
        (purchasesToDisplay && purchasesToDisplay.length ? (
          <>
            <div className={styles.summaryContainer}>
              <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                <span className={styles.cardTitle}>{tCommon('Total Amount')}</span>
                <div className={styles.cardValue}>
                  {`${shop?.currency || ''} ${purchaseAmountSum}`}
                </div>
              </div>

              <div className={`${styles.summaryCard} ${styles.cardGreen}`}>
                <span className={styles.cardTitle}>{tCommon('Total Purchases')}</span>
                <div className={styles.cardValue}>
                  {purchasesToDisplay.length}
                </div>
              </div>
            </div>

            <div className={styles.tableContainer}>
              <Grayout open={isShowGenerateButton} />
              <DataTable
                columns={headerData}
                rows={purchasesToDisplay}
                summary={{ vendor: tCommon('Total'), total_amount: purchaseAmountSum }}
              />
            </div>
          </>
        ) : (
          !loadingIndicator && (
            <Info
              title={tReport('You have no purchases to list')}
              content={tReport("You haven't any purchase to list with given properties")}
            />
          )
        ))
      )}
      {viewItems && (
        <ListDialog
          onClose={handleCloseDetailsDialog}
          toggleItemsTab={handleItemsView}
          tableHeader={listHeaderData}
          rowData={viewItems}
          title={listTitle}
          emptyDataTitle={tReport('No purchase items')}
          emptyDataContent=""
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(PurchaseSummary);
